import { Outlet, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../../lib/auth';
import { Sidebar } from './Sidebar';
import { MyAttendanceWidget } from './MyAttendanceWidget';
import { KYCReminderBanner } from './KYCReminderBanner';
import FeedbackWidget from '../FeedbackWidget';
import { Toaster, toast } from 'sonner';
import { Loader2, Database } from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../lib/api';

export const DashboardLayout = () => {
  const { user, loading } = useAuth();
  const [seeding, setSeeding] = useState(false);
  // v38.11 — Seed Demo Data is gated by `GET /api/admin/demo-seed-allowed`,
  // which reads `DISABLE_DEMO_SEED` / `ALLOW_DEMO_SEED` env vars on the
  // backend. Production runs with `DISABLE_DEMO_SEED=true`, so the button
  // never renders there. Preview defaults to allowed.
  const [seedAllowed, setSeedAllowed] = useState(false);

  // v38.6 — Dark theme removed (user feedback: not readable/user-friendly). Force light
  // mode and scrub any stale `dark` class / localStorage value that older builds may have set.
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    try { localStorage.removeItem('hms-theme'); } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get('/admin/demo-seed-allowed')
      .then((res) => setSeedAllowed(!!res.data?.allowed))
      .catch(() => setSeedAllowed(false));
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // v37 fix (May 22, 2026) — Emergent Google OAuth redirects to `{origin}/dashboard#session_id=…`
  if (!user && typeof window !== 'undefined' && (window.location.hash || '').includes('session_id=')) {
    const hash = window.location.hash;
    return <Navigate to={`/auth/callback${hash}`} replace />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Force password change on first login or after Reset Access
  if (user.must_change_password) {
    return <Navigate to="/change-password" replace state={{ firstLogin: true }} />;
  }

  const handleSeedDemo = async () => {
    if (!window.confirm('Seed demo data?\n\nThis will populate the TEST database with sample patients, visits, vitals, prescriptions, labs, invoices, attendance, and payments for QA / demo purposes.\n\nIt is permanently disabled on production.')) return;
    setSeeding(true);
    try {
      const res = await api.post('/patient-enhancements/seed-demo-data');
      toast.success(res.data?.message || 'Demo data seeded successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to seed data');
    } finally { setSeeding(false); }
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="dashboard-layout">
      <Sidebar />

      <div className="ml-64">
        <header className="sticky top-0 z-40 w-full border-b border-slate-200 bg-white/80 backdrop-blur-md">
          <div className="flex h-16 items-center justify-between px-6">
            <div>
              {/* Breadcrumb can go here */}
            </div>
            <div className="flex items-center gap-3">
              <MyAttendanceWidget />
              {seedAllowed && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSeedDemo}
                  disabled={seeding}
                  className="text-xs gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50"
                  title="Populate the TEST database with demo data for QA. Disabled in production."
                  data-testid="global-seed-demo-btn"
                >
                  {seeding ? <Loader2 size={14} className="animate-spin" /> : <Database size={14} />}
                  {seeding ? 'Seeding…' : 'Seed Demo Data'}
                </Button>
              )}
              <span className="text-sm text-slate-600">
                Welcome, <span className="font-medium text-slate-900">{user.first_name}</span>
              </span>
            </div>
          </div>
        </header>

        <main className="p-6 md:p-8">
          <div className="mx-auto max-w-7xl">
            <KYCReminderBanner />
            <Outlet />
          </div>
        </main>
      </div>

      <Toaster position="top-right" richColors theme="light" />
      <FeedbackWidget />
    </div>
  );
};

export default DashboardLayout;
